import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { AppLayout } from '@/components/AppLayout';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';

interface CreditTransaction {
  id: string;
  transaction_type: string;
  report_type: string | null;
  report_version: string | null;
  credits_charged: number;
  credits_after: number;
  refunded: boolean;
  description: string | null;
  created_at: string;
}

const JobIntelCreditHistory = () => {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const fetchTransactions = async () => {
      const { data, error } = await supabase
        .from('jobintel_credit_transactions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Failed to fetch transactions:', error);
      } else {
        setTransactions((data as any[]) || []);
      }
      setLoading(false);
    };

    fetchTransactions();
  }, [user]);

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'deduction':
        return <Badge variant="destructive" className="text-xs">Deduction</Badge>;
      case 'refund':
        return <Badge className="bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] text-xs">Refund</Badge>;
      case 'refresh':
        return <Badge className="bg-blue-500 text-white text-xs">Refresh</Badge>;
      case 'adjustment':
        return <Badge variant="secondary" className="text-xs">Adjustment</Badge>;
      default:
        return <Badge variant="outline" className="text-xs">{type}</Badge>;
    }
  };

  const formatCredits = (type: string, amount: number) => {
    if (type === 'deduction') {
      return <span className="text-destructive font-semibold">-{Math.abs(amount)}</span>;
    }
    if (type === 'refund' || type === 'refresh') {
      return <span className="text-[hsl(var(--success))] font-semibold">+{Math.abs(amount)}</span>;
    }
    return <span className="font-semibold">{amount > 0 ? `+${amount}` : amount}</span>;
  };

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">JobIntel 360 Credit History</h1>
          <p className="text-muted-foreground text-sm mt-1">
            View all credit transactions for your JobIntel 360 account.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            No credit transactions yet.
          </div>
        ) : (
          <div className="border border-[hsl(var(--card-border))] rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Report</TableHead>
                  <TableHead className="text-right">Credits</TableHead>
                  <TableHead className="text-right">Balance After</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((tx) => (
                  <TableRow key={tx.id}>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {format(new Date(tx.created_at), 'MMM d, yyyy h:mm a')}
                    </TableCell>
                    <TableCell>{getTypeBadge(tx.transaction_type)}</TableCell>
                    <TableCell className="text-sm max-w-xs truncate">
                      {tx.description || '—'}
                      {tx.refunded && (
                        <Badge variant="outline" className="ml-2 text-xs">Refunded</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {tx.report_type ? (
                        <span>{tx.report_type.replace(/_/g, ' ')} ({tx.report_version})</span>
                      ) : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCredits(tx.transaction_type, tx.credits_charged)}
                    </TableCell>
                    <TableCell className="text-right font-medium">{tx.credits_after}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default JobIntelCreditHistory;
